// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { CloseOutlined } from '@ant-design/icons';
import { Button } from '@imprivata-cloud/components';
import { Form, message, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import type { UploadChangeParam } from 'antd/es/upload/interface';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RemoveImg from '../../assets/icons/revoke-consent.svg?react';
import UploadImg from '../../assets/icons/upload.svg?react';

import './CsvUpload.less';

const TRANSLATION_ROOT = 'portal.settings.identity-users.import-users-modal';
const csvFileInput = 'csv-file-input';
const FILE_SIZE_LIMIT_KB = 64;

export type CsvUploadProps = {
  isLoading?: boolean;
  fieldName: string;
  fieldValue: File | undefined;
  onFileChange?: (file?: File) => void;
};

export function CsvUpload({
  isLoading = false,
  fieldName,
  fieldValue,
  onFileChange,
}: Readonly<CsvUploadProps>) {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string | undefined>();

  const setFileNameFromFile = useCallback((file?: File) => {
    setFileName(file?.name);
  }, []);

  const isValidCsv = (file: RcFile) => {
    if (file.size > FILE_SIZE_LIMIT_KB * 1024) {
      void message.warning(
        t(`${TRANSLATION_ROOT}.validation.file-size`, { fileSize: `${FILE_SIZE_LIMIT_KB} KB` }),
      );
      return false;
    }
    if (!(file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      void message.warning(t(`${TRANSLATION_ROOT}.validation.file-type`));
      return false;
    }
    return true;
  };

  useEffect(() => {
    setFileNameFromFile(fieldValue);
  }, [fieldValue, setFileNameFromFile]);

  const handleFileSelected = (file?: File) => {
    if (!file) {
      return;
    }
    setFileNameFromFile(file);
    onFileChange?.(file);
  };

  const onRemove = () => {
    setFileName(undefined);
    onFileChange?.(undefined);
    const input = document.getElementById(csvFileInput) as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  };

  return (
    <Form.Item
      name={fieldName}
      valuePropName="file"
      getValueFromEvent={(e: UploadChangeParam) => e?.file?.originFileObj}
    >
      {/* Hidden input for drag and drop */}
      <input
        id={csvFileInput}
        type="file"
        accept=".csv"
        hidden
        onChange={e => {
          const file = e.target.files?.[0];
          if (!file) {
            return;
          }
          if (!isValidCsv(file as RcFile)) {
            // So that you can try the same file again
            e.target.value = '';
            return;
          }
          handleFileSelected(file);
        }}
      />

      <Upload
        accept=".csv"
        showUploadList={false}
        openFileDialogOnClick={false}
        beforeUpload={file => {
          if (!isValidCsv(file)) {
            return Upload.LIST_IGNORE;
          }
          handleFileSelected(file);
          return false;
        }}
        className="csv-upload-container"
      >
        <div className="csv-upload">
          <div className="csv-upload-dropzone">
            {/* BEFORE selecting a file */}
            {!fileName && (
              <>
                <div className="csv-upload-icon">
                  <UploadImg />
                </div>

                <div className="csv-upload-text">{t(`${TRANSLATION_ROOT}.csv-drag-drop`)}</div>

                <Button
                  type="link"
                  className="csv-upload-browse"
                  label={t(`${TRANSLATION_ROOT}.csv-browse-button`)}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    document.getElementById(csvFileInput)?.click();
                  }}
                />
              </>
            )}

            {/* AFTER selecting a file */}
            {fileName && !isLoading && (
              <div className="csv-upload-selected">
                {/* file name and icons */}
                <div className="csv-upload-selected-info">
                  <RemoveImg className="csv-upload-selected-remove" />
                  <span className="csv-upload-selected-filename">{fileName}</span>
                  <CloseOutlined onClick={onRemove} />
                </div>

                <Button
                  type="link"
                  className="csv-upload-browse"
                  label={t(`${TRANSLATION_ROOT}.csv-new-file`)}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    document.getElementById(csvFileInput)?.click();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Upload>
    </Form.Item>
  );
}

export default CsvUpload;
